import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Microscope, Upload, Loader2, User, Plus, ChevronLeft, FileText,
  Camera, Activity, TrendingUp, Sparkles, Trash2, Clock, AlertCircle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ================= CLIENT LIST =================
function ClientList({ onSelect, onNew }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/skin-analysis/clients`);
        const data = await res.json();
        setClients(data.clients || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-200">Clienti</h2>
        <Button data-testid="new-client-btn" onClick={onNew} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm">
          <Plus className="w-4 h-4 mr-1" /> Nuovo Cliente
        </Button>
      </div>
      {loading ? <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div> : (
        clients.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <User className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>Nessun cliente registrato</p>
            <p className="text-sm mt-1">Crea il primo cliente per iniziare le analisi</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {clients.map((c) => (
              <button key={c.id} data-testid={`client-card-${c.id}`} onClick={() => onSelect(c.id)}
                className="text-left bg-[#12122a] border border-gray-800 rounded-xl p-4 hover:border-emerald-600/50 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-600/20 flex items-center justify-center text-emerald-400 font-semibold text-sm">
                    {(c.nome?.[0] || "")}{(c.cognome?.[0] || "")}
                  </div>
                  <div>
                    <p className="font-medium text-gray-200 group-hover:text-emerald-400 transition-colors">
                      {c.nome} {c.cognome}
                    </p>
                    <p className="text-xs text-gray-500">
                      {c.eta ? `${c.eta} anni` : ""} {c.sessions_count ? `- ${c.sessions_count} analisi` : ""}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ================= NEW CLIENT FORM =================
function NewClientForm({ onBack, onCreated }) {
  const [form, setForm] = useState({ nome: "", cognome: "", eta: "", genere: "", telefono: "", email: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!form.nome.trim() || !form.cognome.trim()) { setError("Nome e cognome sono obbligatori"); return; }
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/api/skin-analysis/clients`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, eta: form.eta ? parseInt(form.eta) : null })
      });
      const text = await res.text();
      if (!res.ok) { try { throw new Error(JSON.parse(text).detail); } catch (pe) { if (pe instanceof SyntaxError) throw new Error("Errore"); throw pe; } }
      const data = JSON.parse(text);
      onCreated(data.id);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors">
        <ChevronLeft className="w-4 h-4" /> Torna ai clienti
      </button>
      <Card className="bg-[#12122a] border-gray-800 max-w-lg">
        <CardHeader><CardTitle className="text-lg text-gray-200 flex items-center gap-2"><User className="w-5 h-5 text-emerald-400" /> Nuovo Cliente</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Nome *</label>
              <Input data-testid="client-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className="bg-[#0d0d20] border-gray-700 text-white focus:border-emerald-500" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Cognome *</label>
              <Input data-testid="client-cognome" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })}
                className="bg-[#0d0d20] border-gray-700 text-white focus:border-emerald-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Eta</label>
              <Input data-testid="client-eta" type="number" value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })}
                className="bg-[#0d0d20] border-gray-700 text-white focus:border-emerald-500" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Genere</label>
              <select data-testid="client-genere" value={form.genere} onChange={(e) => setForm({ ...form, genere: e.target.value })}
                className="w-full h-10 px-3 bg-[#0d0d20] border border-gray-700 rounded-md text-white text-sm focus:border-emerald-500">
                <option value="">-</option><option value="F">Donna</option><option value="M">Uomo</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Telefono</label>
              <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                className="bg-[#0d0d20] border-gray-700 text-white focus:border-emerald-500" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Email</label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-[#0d0d20] border-gray-700 text-white focus:border-emerald-500" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Note</label>
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="bg-[#0d0d20] border-gray-700 text-white min-h-[60px] resize-none focus:border-emerald-500" />
          </div>
          {error && <div className="text-red-400 text-sm bg-red-900/20 border border-red-800/50 rounded-lg px-3 py-2">{error}</div>}
          <Button data-testid="save-client-btn" onClick={handleSave} disabled={saving}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva Cliente"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ================= CLIENT DETAIL =================
function ClientDetail({ clientId, onBack }) {
  const [client, setClient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  const fetchClient = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/skin-analysis/clients/${clientId}`);
      const data = await res.json();
      setClient(data.client);
      setSessions(data.sessions || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { fetchClient(); }, [fetchClient]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!client) return <p className="text-red-400">Cliente non trovato</p>;

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors">
        <ChevronLeft className="w-4 h-4" /> Torna ai clienti
      </button>

      {/* Client Header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-emerald-600/20 flex items-center justify-center text-emerald-400 font-bold text-xl">
          {(client.nome?.[0] || "")}{(client.cognome?.[0] || "")}
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-100">{client.nome} {client.cognome}</h2>
          <p className="text-sm text-gray-500">
            {client.eta ? `${client.eta} anni` : ""} {client.genere ? `- ${client.genere === "F" ? "Donna" : "Uomo"}` : ""}
            {client.telefono ? ` - ${client.telefono}` : ""}
          </p>
        </div>
        <Button data-testid="new-analysis-btn" onClick={() => setShowAnalysis(true)}
          className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white">
          <Camera className="w-4 h-4 mr-1" /> Nuova Analisi
        </Button>
      </div>

      {showAnalysis && (
        <NewAnalysisForm clientId={clientId} clientInfo={client}
          onDone={() => { setShowAnalysis(false); fetchClient(); }} onCancel={() => setShowAnalysis(false)} />
      )}

      {selectedSession && (
        <SessionDetail session={selectedSession} clientInfo={client} onClose={() => setSelectedSession(null)} />
      )}

      {/* Sessions Timeline */}
      {!selectedSession && !showAnalysis && (
        <div>
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Storico Analisi ({sessions.length})
          </h3>
          {sessions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Microscope className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p>Nessuna analisi ancora</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sessions.map((s, i) => (
                <button key={s.session_id || i} data-testid={`session-${i}`} onClick={() => setSelectedSession(s)}
                  className="w-full text-left bg-[#12122a] border border-gray-800 rounded-xl p-4 hover:border-emerald-600/50 transition-all">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                        <Activity className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-200">
                          Analisi {s.analysis_type === "capelli" ? "Capelli" : "Pelle"} #{sessions.length - i}
                        </p>
                        <p className="text-xs text-gray-500">{new Date(s.date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {s.images?.length > 0 && <span className="text-[10px] text-gray-500 bg-gray-800 px-2 py-0.5 rounded">{s.images.length} img</span>}
                      <ChevronLeft className="w-4 h-4 text-gray-600 rotate-180" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ================= NEW ANALYSIS FORM =================
function NewAnalysisForm({ clientId, clientInfo, onDone, onCancel }) {
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [analysisType, setAnalysisType] = useState("pelle");
  const [note, setNote] = useState("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  const handleImages = (e) => {
    const files = Array.from(e.target.files);
    setImages((prev) => [...prev, ...files]);
    setPreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
  };

  const handleSubmit = async () => {
    if (images.length === 0) { setError("Carica almeno un'immagine"); return; }
    setProcessing(true); setError("");
    try {
      const fd = new FormData();
      images.forEach((img) => fd.append("images", img));
      fd.append("analysis_type", analysisType);
      fd.append("note", note);
      const res = await fetch(`${API}/api/skin-analysis/clients/${clientId}/sessions`, { method: "POST", body: fd });
      const text = await res.text();
      if (!res.ok) { try { throw new Error(JSON.parse(text).detail); } catch (pe) { if (pe instanceof SyntaxError) throw new Error("Errore analisi"); throw pe; } }
      onDone();
    } catch (e) { setError(e.message); }
    finally { setProcessing(false); }
  };

  return (
    <Card className="bg-[#12122a] border-emerald-800/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg text-gray-200 flex items-center gap-2">
          <Camera className="w-5 h-5 text-emerald-400" /> Nuova Analisi per {clientInfo.nome}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          {["pelle", "capelli"].map((t) => (
            <button key={t} data-testid={`type-${t}`} onClick={() => setAnalysisType(t)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                analysisType === t ? "bg-emerald-600 text-white" : "bg-gray-800 text-gray-400 hover:text-white"
              }`}>
              {t === "pelle" ? "Analisi Pelle" : "Analisi Capelli"}
            </button>
          ))}
        </div>

        <div>
          <label className="text-sm text-gray-400 mb-2 block">Immagini dal software APM Pro</label>
          <label data-testid="upload-analysis-images" className="flex items-center gap-2 px-6 py-4 bg-[#0d0d20] border-2 border-dashed border-gray-600 rounded-xl cursor-pointer hover:border-emerald-500 transition-colors justify-center">
            <Upload className="w-5 h-5 text-emerald-400" />
            <span className="text-gray-300">Carica immagini analisi</span>
            <input type="file" accept="image/*" multiple onChange={handleImages} className="hidden" />
          </label>
          {previews.length > 0 && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {previews.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p} alt={`img-${i}`} className="w-20 h-20 object-cover rounded-lg border border-gray-700" />
                  <button onClick={() => { setImages((prev) => prev.filter((_, j) => j !== i)); setPreviews((prev) => prev.filter((_, j) => j !== i)); }}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center">
                    <Trash2 className="w-3 h-3 text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="text-xs text-gray-400 mb-1 block">Note sessione</label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note opzionali sulla sessione..."
            className="bg-[#0d0d20] border-gray-700 text-white min-h-[60px] resize-none focus:border-emerald-500" />
        </div>

        {error && <div className="text-red-400 text-sm bg-red-900/20 border border-red-800/50 rounded-lg px-3 py-2">{error}</div>}

        <div className="flex gap-2">
          <Button onClick={onCancel} variant="outline" className="border-gray-700 text-gray-400 hover:text-white">Annulla</Button>
          <Button data-testid="start-analysis-btn" onClick={handleSubmit} disabled={processing || images.length === 0}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">
            {processing ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> AI sta analizzando...</span>
              : <span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> Analizza con AI</span>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ================= SESSION DETAIL =================
function SessionDetail({ session, clientInfo, onClose }) {
  const analysis = session.analysis || {};
  const rawData = session.raw_data?.dati_estratti || {};

  const statusColor = (stato) => {
    if (stato === "buono") return "text-green-400 bg-green-900/20 border-green-800/50";
    if (stato === "medio") return "text-amber-400 bg-amber-900/20 border-amber-800/50";
    if (stato === "critico") return "text-red-400 bg-red-900/20 border-red-800/50";
    return "text-gray-400 bg-gray-900/20 border-gray-800/50";
  };

  return (
    <div className="space-y-5">
      <button onClick={onClose} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white">
        <ChevronLeft className="w-4 h-4" /> Torna allo storico
      </button>

      <div className="flex items-center gap-3 mb-2">
        <h3 className="text-lg font-bold text-gray-100">
          Analisi {session.analysis_type === "capelli" ? "Capelli" : "Pelle"} - {clientInfo.nome} {clientInfo.cognome}
        </h3>
        <span className="text-xs text-gray-500">{new Date(session.date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</span>
      </div>

      {/* Images */}
      {session.images?.length > 0 && (
        <div className="flex gap-3 flex-wrap">
          {session.images.map((img, i) => (
            <img key={i} src={`${API}${img.url}`} alt={`analisi-${i}`}
              className="h-32 rounded-xl border border-gray-700 object-cover" />
          ))}
        </div>
      )}

      {/* Raw Data */}
      <Card className="bg-[#12122a] border-gray-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-gray-200 flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-400" /> Dati Estratti (JSON)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {Object.entries(rawData).map(([key, val]) => (
              <div key={key} className="bg-[#0d0d20] border border-gray-800 rounded-lg px-3 py-2">
                <p className="text-[10px] text-gray-500 uppercase">{key.replace(/_/g, " ")}</p>
                <p className={`text-sm font-mono ${val === "non disponibile" ? "text-gray-600 italic" : "text-emerald-400"}`}>
                  {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Professional Analysis */}
      {analysis.analisi_dettagliata && (
        <Card className="bg-[#12122a] border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-gray-200 flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-400" /> Analisi Professionale
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {analysis.analisi_dettagliata.map((item, i) => (
              <div key={i} className={`border rounded-lg px-3 py-2.5 ${statusColor(item.stato)}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm capitalize">{item.parametro}</span>
                  <span className="text-xs font-semibold uppercase px-2 py-0.5 rounded-full bg-black/20">{item.stato}</span>
                </div>
                <p className="text-xs opacity-80">{item.spiegazione}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Diagnosis */}
      {analysis.diagnosi_generale && (
        <Card className="bg-[#12122a] border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-gray-200 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400" /> Diagnosi Generale
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-300 leading-relaxed">{analysis.diagnosi_generale}</p>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {analysis.consigli && (
        <Card className="bg-[#12122a] border-gray-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-gray-200 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-400" /> Consigli Personalizzati
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {analysis.consigli.skincare_routine?.length > 0 && (
              <div>
                <p className="text-xs text-emerald-400 font-semibold uppercase mb-1">Skincare Routine</p>
                <ol className="text-sm text-gray-300 space-y-1 list-decimal ml-4">
                  {analysis.consigli.skincare_routine.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </div>
            )}
            {analysis.consigli.haircare_routine?.length > 0 && (
              <div>
                <p className="text-xs text-violet-400 font-semibold uppercase mb-1">Haircare Routine</p>
                <ol className="text-sm text-gray-300 space-y-1 list-decimal ml-4">
                  {analysis.consigli.haircare_routine.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </div>
            )}
            {analysis.consigli.trattamenti_consigliati?.length > 0 && (
              <div>
                <p className="text-xs text-amber-400 font-semibold uppercase mb-1">Trattamenti Consigliati</p>
                <ul className="text-sm text-gray-300 space-y-1 list-disc ml-4">
                  {analysis.consigli.trattamenti_consigliati.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Video Text */}
      {analysis.testo_video && (
        <Card className="bg-[#12122a] border-emerald-800/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-gray-200 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-fuchsia-400" /> Testo per Video
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-300 leading-relaxed italic">"{analysis.testo_video}"</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ================= MAIN PAGE =================
export default function SkinAnalysisPage() {
  const [view, setView] = useState("list"); // list, new, detail
  const [selectedClientId, setSelectedClientId] = useState(null);

  return (
    <div className="min-h-screen bg-[#0a0a1e] text-white p-4 md:p-8" data-testid="skin-analysis-page">
      <div className="max-w-5xl mx-auto mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl">
            <Microscope className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
            Analisi Pelle & Capelli
          </h1>
        </div>
        <p className="text-gray-400 text-sm ml-14">
          Sistema di analisi professionale con AI Vision - APM Pro 300
        </p>
      </div>

      <div className="max-w-5xl mx-auto">
        {view === "list" && (
          <ClientList
            onSelect={(id) => { setSelectedClientId(id); setView("detail"); }}
            onNew={() => setView("new")}
          />
        )}
        {view === "new" && (
          <NewClientForm
            onBack={() => setView("list")}
            onCreated={(id) => { setSelectedClientId(id); setView("detail"); }}
          />
        )}
        {view === "detail" && selectedClientId && (
          <ClientDetail
            clientId={selectedClientId}
            onBack={() => { setSelectedClientId(null); setView("list"); }}
          />
        )}
      </div>
    </div>
  );
}
